import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  View,
  StyleSheet,
  ScrollView,
  Text,
  ActivityIndicator,
  TouchableOpacity,
  Alert,
} from "react-native";

// Import components
import PatientDetailCard from "./PatientDetailCard";
import VoiceNotesList from "./voice-notes/VoiceNotesList";
import SimulateConsults from "./SimulateConsults";
import PatientImagesView from "./PatientImagesView"; // Import the new component
import Tab2 from "../../../common/native/Tab2";
import ImageUploadComponent from "../../../common/native/ImageUpload";

// Import utilities & services
import { trackEvent } from "../../../../utils/mixpanel";
import { useImageUpload } from "../../../../utils/imageUpload";
import { isDebugQueryEnabled } from "../../../../utils/debugQuery";
import {
  getPatientImages,
  patchPatientImage,
  reprocessClinicImages,
} from "../../../../services/api-clinic";

const PatientPlanningView = ({
  selectedPatient,
  voiceNoteRefreshKey,
  setVoiceNoteRefreshKey,
  setSelectedPhotos,
  setSelectedPhotoThumbnails,
  setSelectedPatientName,
  setSelectedPhotoIndex,
  setSelectedPhotoEditTargets,
  setSelectedPhotoMetadata,
  setGalleryModalVisible,
  session,
  currentView,
  setSelectedPatient, // Added prop to update patient data in parent
  tipsEnabled,
  photoEditRefreshKey = 0,
}) => {
  // Add local state for image loading and data
  const [isLoadingImages, setIsLoadingImages] = useState(false);
  const [isValidatingImages, setIsValidatingImages] = useState(false);
  const [isReprocessingThumbnails, setIsReprocessingThumbnails] =
    useState(false);
  const [activeTab, setActiveTab] = useState("Notes");
  const [patientImages, setPatientImages] = useState([]);
  const [updatingImageStageIds, setUpdatingImageStageIds] = useState({});
  const [validUrls, setValidUrls] = useState({});
  const checkedUrlsRef = useRef({});

  const excludedUploadTypes = useMemo(
    () => new Set(["TransformImageForForm", "TransformImageForUser"]),
    [],
  );

  const isImageLoadable = useCallback(async (url, options = {}) => {
    const { timeoutMs = 5000 } = options;
    if (!url) return false;
    if (checkedUrlsRef.current[url] !== undefined) {
      return checkedUrlsRef.current[url];
    }

    try {
      const response = await fetch(url, {
        method: "GET",
        cache: "no-store",
        signal: AbortSignal.timeout(timeoutMs),
      });
      const result = response.ok;
      // Cache only successful checks so transient failures/timeouts
      // can be retried during the same session.
      if (result) {
        checkedUrlsRef.current[url] = true;
      }
      return result;
    } catch (error) {
      return false;
    }
  }, []);

  const trimPresignedImageUrl = useCallback((url) => {
    if (!url || typeof url !== "string") return "";
    const match = url.match(/^(.*?\.(?:png|jpe?g))/i);
    if (match?.[1]) return match[1];
    return url.split("?")[0];
  }, []);

  // Add a ref to track which patient we've already fetched images for
  const fetchedImagesForPatientRef = useRef(null);

  const fullImageUrlMap = useMemo(() => {
    return patientImages.reduce((acc, item) => {
      if (excludedUploadTypes.has(item?.image_data?.upload_type)) return acc;
      const id = String(item?.image_data?.id ?? "");
      const fullUrl = item?.presigned_urls?.[0];
      if (id && fullUrl) acc[id] = fullUrl;
      return acc;
    }, {});
  }, [patientImages, excludedUploadTypes]);

  const filteredImages = useMemo(() => {
    return patientImages.reduce((acc, item) => {
      if (excludedUploadTypes.has(item?.image_data?.upload_type)) return acc;
      const id = String(item?.image_data?.id ?? "");
      const resolvedImageUrl = validUrls[id];
      if (!resolvedImageUrl) return acc;

      acc.push({
        ...item,
        resolved_image_url: resolvedImageUrl,
      });
      return acc;
    }, []);
  }, [patientImages, validUrls, excludedUploadTypes]);

  const galleryImageEntries = useMemo(() => {
    return filteredImages
      .map((item) => {
        const id = String(item?.image_data?.id ?? "");
        const fullUrl = fullImageUrlMap[id] ?? "";
        const thumbnailUrl = item?.thumbnail_presigned_urls?.[0] ?? "";
        const validatedUrl = validUrls[id] ?? "";
        const metadata = item?.image_data || {};

        return {
          id,
          url: fullUrl || thumbnailUrl,
          thumbnailUrl: validatedUrl === thumbnailUrl ? thumbnailUrl : "",
          metadata: {
            procedure_types: metadata.procedure_types,
            procedure_types_human_readable:
              metadata.procedure_types_human_readable,
            view_type: metadata.view_type,
            view_type_human_readable: metadata.view_type_human_readable,
            surgery_stage: metadata.surgery_stage,
            external_id: metadata.external_id,
            tags: metadata.tags,
            created_at: metadata.created_at,
            updated_at: metadata.updated_at,
            date_taken: metadata.date_taken,
          },
        };
      })
      .filter((entry) => Boolean(entry.id && entry.url));
  }, [filteredImages, fullImageUrlMap, validUrls]);

  useEffect(() => {
    if (photoEditRefreshKey <= 0 || galleryImageEntries.length === 0) return;

    setSelectedPhotos(galleryImageEntries.map((item) => item.url));
    setSelectedPhotoThumbnails(
      galleryImageEntries.map((item) => item.thumbnailUrl),
    );
    setSelectedPhotoEditTargets(
      galleryImageEntries.map((item) => ({
        targetType: "clinic_patient_image",
        targetId: Number(item.id),
      })),
    );
    setSelectedPhotoMetadata(galleryImageEntries.map((item) => item.metadata));
  }, [
    photoEditRefreshKey,
    galleryImageEntries,
    setSelectedPhotos,
    setSelectedPhotoThumbnails,
    setSelectedPhotoEditTargets,
    setSelectedPhotoMetadata,
  ]);

  // Fetch (or refetch) the patient's images from the backend
  const fetchPatientPhotos = useCallback(async () => {
    if (!selectedPatient?.clinicOSUUID || !session?.clinicId) {
      return;
    }

    try {
      setIsLoadingImages(true);
      const fetchedImagesArray = await getPatientImages(
        session,
        selectedPatient.clinicOSUUID,
      );

      // If we got images, store them in local state
      if (fetchedImagesArray && fetchedImagesArray.length > 0) {
        setPatientImages(fetchedImagesArray);

        trackEvent(
          "Patient Images Loaded",
          {
            "Patient ID": selectedPatient.clinicOSUUID,
            "Patient Name": selectedPatient.name,
            "Image Count": fetchedImagesArray.length,
          },
          session,
        );
      } else {
        // No images found
        setPatientImages([]);
      }

      // Mark that we've fetched images for this patient
      fetchedImagesForPatientRef.current = selectedPatient.clinicOSUUID;
    } catch (error) {
      console.error("Error fetching patient images:", error);

      trackEvent(
        "Patient Images Error",
        {
          "Patient ID": selectedPatient.clinicOSUUID,
          "Patient Name": selectedPatient.name,
          Error: error.message,
        },
        session,
      );
    } finally {
      setIsLoadingImages(false);
    }
  }, [
    selectedPatient?.clinicOSUUID,
    selectedPatient?.id,
    selectedPatient?.name,
    session,
  ]);

  // Effect to fetch patient images when planning view loads
  useEffect(() => {
    if (photoEditRefreshKey > 0) {
      fetchedImagesForPatientRef.current = null;
    }

    // Only proceed if we have a valid patient with a clinicOS UUID
    // AND we haven't already fetched images for this patient
    if (
      !selectedPatient ||
      !selectedPatient.clinicOSUUID ||
      !session?.clinicId ||
      fetchedImagesForPatientRef.current === selectedPatient.clinicOSUUID
    ) {
      return;
    }

    fetchPatientPhotos();
  }, [
    selectedPatient?.clinicOSUUID,
    session?.clinicId,
    fetchPatientPhotos,
    photoEditRefreshKey,
  ]);

  // Image upload (mirrors the "Upload Patient Images" section in Leads & Patients)
  const {
    state: uploadState,
    manager: uploadManager,
    uploadImages,
  } = useImageUpload();

  const handleUpload = useCallback(async () => {
    if (!session || !selectedPatient?.clinicOSUUID) return;

    await uploadImages(session, selectedPatient.clinicOSUUID, () => {
      // On success, refetch the patient's images so the new ones appear
      fetchedImagesForPatientRef.current = null;
      fetchPatientPhotos();
    });
  }, [
    session,
    selectedPatient?.clinicOSUUID,
    uploadImages,
    fetchPatientPhotos,
  ]);

  // Reset local state and ref when selectedPatient changes entirely (different ID)
  useEffect(() => {
    let isCancelled = false;

    const validateImageUrls = async () => {
      if (!patientImages.length) {
        setValidUrls({});
        return;
      }

      setIsValidatingImages(true);
      const nextValidUrls = {};
      const missingThumbnailImages = [];
      const batchSize = 5;

      for (let i = 0; i < patientImages.length; i += batchSize) {
        if (isCancelled) return;

        const batch = patientImages.slice(i, i + batchSize);
        const batchResults = await Promise.allSettled(
          batch.map(async (item) => {
            if (excludedUploadTypes.has(item?.image_data?.upload_type)) {
              return null;
            }

            const id = String(item?.image_data?.id ?? "");
            if (!id) return null;

            const thumbnailUrl = item?.thumbnail_presigned_urls?.[0];
            const fullUrl = item?.presigned_urls?.[0];
            let usableUrl = null;

            if (
              thumbnailUrl &&
              (await isImageLoadable(thumbnailUrl, { timeoutMs: 5000 }))
            ) {
              usableUrl = thumbnailUrl;
            } else if (
              fullUrl &&
              (await isImageLoadable(fullUrl, { timeoutMs: 15000 }))
            ) {
              usableUrl = fullUrl;
            }

            if (!usableUrl) {
              missingThumbnailImages.push({
                id,
                thumbnailUrl: trimPresignedImageUrl(thumbnailUrl || ""),
                reason: thumbnailUrl
                  ? "thumbnail_not_loadable_and_full_not_loadable"
                  : "thumbnail_missing_and_full_not_loadable",
                imageUrl: trimPresignedImageUrl(fullUrl || ""),
              });
            }

            return usableUrl ? { id, url: usableUrl } : null;
          }),
        );

        batchResults.forEach((result) => {
          if (result.status === "fulfilled" && result.value) {
            const { id, url } = result.value;
            nextValidUrls[id] = url;
          }
        });
      }

      if (!isCancelled) {
        setValidUrls(nextValidUrls);
        if (missingThumbnailImages.length > 0) {
          console.log(
            "[Consults] Images without available thumbnails:",
            missingThumbnailImages,
          );
        }
        setIsValidatingImages(false);
      }
    };

    validateImageUrls();

    return () => {
      isCancelled = true;
    };
  }, [
    patientImages,
    excludedUploadTypes,
    isImageLoadable,
    trimPresignedImageUrl,
  ]);

  useEffect(() => {
    checkedUrlsRef.current = {};
    setValidUrls({});
    setSelectedPhotoIndex(0);
    setSelectedPhotoEditTargets([]);
    setIsValidatingImages(false);
  }, [
    selectedPatient?.clinicOSUUID,
    setSelectedPhotoIndex,
    setSelectedPhotoEditTargets,
  ]);

  useEffect(() => {
    // If we have a new patient with a different clinicOSUUID, reset everything
    if (selectedPatient?.clinicOSUUID !== fetchedImagesForPatientRef.current) {
      // Only reset if the new ID is different and not null
      if (selectedPatient?.clinicOSUUID) {
        // Reset local state and ref - that happens before fetch
        fetchedImagesForPatientRef.current = null;
        setPatientImages([]);
      }
    }
  }, [selectedPatient?.id]); // Only run when patient ID changes

  const suggestedImageUrls = Array.from(
    new Set(
      galleryImageEntries
        .map((item) => item.url)
        .filter((url) => typeof url === "string" && url.length > 0),
    ),
  );

  const showDebugActions = useMemo(() => {
    if (typeof window === "undefined") return false;
    const params = new URLSearchParams(window.location.search);
    return isDebugQueryEnabled(params);
  }, []);

  const isFollowUpVisit =
    selectedPatient?.visitType &&
    (selectedPatient.visitType.includes("F/U") ||
      selectedPatient.visitType.toLowerCase().includes("follow up"));

  const handleReprocessThumbnails = useCallback(async () => {
    if (!selectedPatient?.clinicOSUUID) {
      Alert.alert("Unable to reprocess", "Missing patient ID.");
      return;
    }

    try {
      setIsReprocessingThumbnails(true);
      const data = await reprocessClinicImages(session, {
        patient_uuid: selectedPatient.clinicOSUUID,
        force: true,
      });

      trackEvent(
        "Patient Thumbnail Reprocess Queued",
        {
          "Patient ID": selectedPatient?.clinicOSUUID,
          "Patient UUID": selectedPatient?.clinicOSUUID,
          "Num Images Queued": data?.num_images ?? 0,
          "Metadata URL": data?.metadata_url ?? null,
        },
        session,
      );

      Alert.alert(
        "Reprocess queued",
        `Queued ${data.num_images} image${data.num_images === 1 ? "" : "s"} for thumbnail reprocessing.`,
      );
    } catch (error) {
      trackEvent(
        "Patient Thumbnail Reprocess Failed",
        {
          "Patient ID": selectedPatient?.clinicOSUUID,
          "Patient UUID": selectedPatient?.clinicOSUUID,
          Error: error?.message || "Unknown error",
        },
        session,
      );
      Alert.alert(
        "Reprocess failed",
        error?.message || "Failed to queue thumbnail reprocessing.",
      );
    } finally {
      setIsReprocessingThumbnails(false);
    }
  }, [selectedPatient?.clinicOSUUID, session]);

  const handleSurgeryStageChange = useCallback(
    async (imageOrImages, nextStage) => {
      const patientId = selectedPatient?.clinicOSUUID;
      const imagesToUpdate = (
        Array.isArray(imageOrImages) ? imageOrImages : [imageOrImages]
      )
        .map((image) => ({
          image,
          imageId: image?.image_data?.id,
          previousStage: image?.image_data?.surgery_stage ?? null,
        }))
        .filter(
          ({ imageId, previousStage }) =>
            imageId && previousStage !== nextStage,
        );

      if (!session || !patientId || imagesToUpdate.length === 0) {
        return;
      }

      const imageIds = imagesToUpdate.map(({ imageId }) => imageId);
      const previousStageByImageId = imagesToUpdate.reduce(
        (acc, { imageId, previousStage }) => ({
          ...acc,
          [imageId]: previousStage,
        }),
        {},
      );

      setUpdatingImageStageIds((prev) => ({
        ...prev,
        ...imageIds.reduce(
          (acc, imageId) => ({
            ...acc,
            [imageId]: true,
          }),
          {},
        ),
      }));

      setPatientImages((prevImages) =>
        prevImages.map((patientImage) =>
          imageIds.includes(patientImage?.image_data?.id)
            ? {
                ...patientImage,
                image_data: {
                  ...patientImage.image_data,
                  surgery_stage: nextStage,
                },
              }
            : patientImage,
        ),
      );

      try {
        await Promise.all(
          imagesToUpdate.map(({ imageId }) =>
            patchPatientImage(session, patientId, imageId, {
              surgery_stage: nextStage,
            }),
          ),
        );

        trackEvent(
          "Patient Image Surgery Stage Updated",
          {
            "Patient ID": selectedPatient?.clinicOSUUID,
            "Patient Name": selectedPatient?.name,
            "Image Count": imageIds.length,
            "Image IDs": imageIds,
            "Previous Surgery Stages": previousStageByImageId,
            "Next Surgery Stage": nextStage,
          },
          session,
        );
      } catch (error) {
        setPatientImages((prevImages) =>
          prevImages.map((patientImage) =>
            imageIds.includes(patientImage?.image_data?.id)
              ? {
                  ...patientImage,
                  image_data: {
                    ...patientImage.image_data,
                    surgery_stage:
                      previousStageByImageId[patientImage.image_data.id],
                  },
                }
              : patientImage,
          ),
        );

        trackEvent(
          "Patient Image Surgery Stage Update Failed",
          {
            "Patient ID": selectedPatient?.clinicOSUUID,
            "Patient Name": selectedPatient?.name,
            "Image Count": imageIds.length,
            "Image IDs": imageIds,
            "Previous Surgery Stages": previousStageByImageId,
            "Next Surgery Stage": nextStage,
            Error: error?.message || "Unknown error",
          },
          session,
        );

        Alert.alert(
          "Stage not updated",
          error?.message ||
            "Unable to update the selected photos. Please try again.",
        );
      } finally {
        setUpdatingImageStageIds((prev) => {
          const next = { ...prev };
          imageIds.forEach((imageId) => {
            delete next[imageId];
          });
          return next;
        });
      }
    },
    [
      selectedPatient?.clinicOSUUID,
      selectedPatient?.id,
      selectedPatient?.name,
      session,
    ],
  );

  return (
    <ScrollView
      style={styles.planningViewContainer}
      showsVerticalScrollIndicator={false}
    >
      {/* Patient Detail Card */}
      <PatientDetailCard
        patient={selectedPatient}
        onVoiceNoteSaved={() => setVoiceNoteRefreshKey((prev) => prev + 1)}
        tipsEnabled={tipsEnabled}
        autoStartRecording={Boolean(selectedPatient?.autoStartRecording)}
        onAutoStartHandled={() =>
          setSelectedPatient((prevPatient) =>
            prevPatient
              ? {
                  ...prevPatient,
                  autoStartRecording: false,
                }
              : prevPatient,
          )
        }
      />

      <View style={styles.sectionContainer}>
        <View style={[styles.sectionHeader, styles.voiceNotesHeader]}>
          <Tab2
            tabs={["Notes", "Photos"]}
            activeTab={activeTab}
            onTabSwitch={setActiveTab}
            containerStyle={styles.tabContainer}
          />
        </View>

        {activeTab === "Notes" ? (
          <View style={styles.sectionContent}>
            <VoiceNotesList
              patient={selectedPatient}
              appointmentId={selectedPatient.appointmentId}
              refreshKey={voiceNoteRefreshKey}
              tipsEnabled={tipsEnabled}
            />
          </View>
        ) : (
          <>
            <View style={styles.sectionContent}>
              <View style={styles.subsectionHeader}>
                <Text style={styles.sectionTitle}>Patient Photos</Text>
                {showDebugActions && (
                  <TouchableOpacity
                    onPress={handleReprocessThumbnails}
                    style={[
                      styles.debugActionButton,
                      isReprocessingThumbnails &&
                        styles.debugActionButtonDisabled,
                    ]}
                    disabled={isReprocessingThumbnails}
                    accessibilityRole="button"
                    accessibilityLabel="Reprocess thumbnails"
                  >
                    <Text style={styles.debugActionButtonText}>
                      {isReprocessingThumbnails
                        ? "Reprocessing..."
                        : "Reprocess thumbnails"}
                    </Text>
                  </TouchableOpacity>
                )}
              </View>
              <PatientImagesView
                images={filteredImages}
                isLoading={isLoadingImages || isValidatingImages}
                onImageTap={(image) => {
                  if (image && galleryImageEntries.length > 0) {
                    const imageId = String(image?.image_data?.id ?? "");
                    const selectedIndex = galleryImageEntries.findIndex(
                      (item) => item.id === imageId,
                    );
                    if (selectedIndex === -1) return;

                    setSelectedPhotos(
                      galleryImageEntries.map((item) => item.url),
                    );
                    setSelectedPhotoThumbnails(
                      galleryImageEntries.map((item) => item.thumbnailUrl),
                    );
                    setSelectedPhotoEditTargets(
                      galleryImageEntries.map((item) => ({
                        targetType: "clinic_patient_image",
                        targetId: Number(item.id),
                      })),
                    );
                    setSelectedPhotoMetadata(
                      galleryImageEntries.map((item) => item.metadata),
                    );
                    setSelectedPhotoIndex(selectedIndex);
                    const viewType =
                      image.image_data?.view_type_human_readable || "Photo";
                    setSelectedPatientName(
                      `${selectedPatient.name} - ${viewType}`,
                    );
                    setGalleryModalVisible(true);

                    trackEvent(
                      "Patient Photo Expanded",
                      {
                        "Patient ID": selectedPatient?.clinicOSUUID,
                        "Patient Name": selectedPatient?.name,
                        "Photo View": viewType,
                        "View Mode": currentView,
                      },
                      session,
                    );
                  }
                }}
                onSurgeryStageChange={handleSurgeryStageChange}
                updatingImageStageIds={updatingImageStageIds}
                trackEvent={trackEvent}
                session={session}
              />
            </View>

            {/* Show loading indicator separately if we're loading images */}
            {isLoadingImages && (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="small" color="#0085FF" />
                <Text style={styles.loadingText}>
                  Loading patient images...
                </Text>
              </View>
            )}

            {/* Simulation Generator - ONLY for regular consultation patients with photos */}
            {patientImages.length > 0 && !isFollowUpVisit && (
              <View style={styles.sectionContent}>
                <View style={styles.subsectionHeader}>
                  <Text style={styles.sectionTitle}>Simulation</Text>
                </View>
                <SimulateConsults
                  patient={selectedPatient}
                  suggestedImageUrls={suggestedImageUrls}
                  tipsEnabled={tipsEnabled}
                />
              </View>
            )}

            {/* Upload Patient Images - same section as in Leads & Patients */}
            <View style={styles.sectionContent}>
              <ImageUploadComponent
                uploadState={uploadState}
                uploadManager={uploadManager}
                onUpload={handleUpload}
              />
            </View>
          </>
        )}
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  // Planning View Styles
  planningViewContainer: {
    padding: 16,
    paddingBottom: 100, // Add padding for Paige
  },
  sectionContainer: {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: "#EEEEEE",
    overflow: "hidden",
  },
  sectionHeader: {
    backgroundColor: "#FFFFFF",
    paddingVertical: 16,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
    borderBottomColor: "#EEEEEE",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  voiceNotesHeader: {
    backgroundColor: "#F9F9F9",
  },
  flexSectionHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: "#FFFFFF",
    paddingVertical: 16,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
    borderBottomColor: "#EEEEEE",
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: "500",
    color: "#333333",
  },
  tabContainer: {
    marginHorizontal: 0,
  },
  subsectionHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },
  debugActionButton: {
    backgroundColor: "#00B7F4",
    borderRadius: 10,
    paddingHorizontal: 24,
    paddingVertical: 12,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
  },
  debugActionButtonDisabled: {
    opacity: 0.6,
  },
  debugActionButtonText: {
    color: "#FFFFFF",
    fontFamily: "Urbanist500",
    textAlign: "center",
    fontSize: 16,
    lineHeight: 22.4,
    letterSpacing: 0.032,
  },
  sectionContent: {
    padding: 16,
  },
  presentationButtonContainer: {
    alignItems: "flex-end",
    marginBottom: 16,
  },
  presentationButton: {
    backgroundColor: "#0085FF",
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 6,
  },
  presentationButtonText: {
    color: "#FFFFFF",
    fontSize: 14,
    fontWeight: "500",
  },

  // Loading and error states
  loadingContainer: {
    padding: 24,
    alignItems: "center",
    justifyContent: "center",
    minHeight: 100,
  },
  loadingText: {
    fontSize: 16,
    color: "#555",
    marginTop: 12,
  },

  // Lists
  photoList: {
    paddingBottom: 16,
  },

  // Empty states
  emptyStateContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
    minHeight: 150,
  },
  emptyStateText: {
    fontSize: 16,
    color: "#777777",
    marginBottom: 16,
    textAlign: "center",
  },
  patientInfoButton: {
    backgroundColor: "#0085FF",
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 6,
  },
  patientInfoButtonText: {
    color: "#FFFFFF",
    fontSize: 14,
    fontWeight: "500",
  },

  // Similar Patients
  albumContainer: {
    marginBottom: 24,
  },
  albumHeader: {
    marginBottom: 12,
  },
  albumTitle: {
    fontSize: 16,
    fontWeight: "500",
    color: "#333333",
  },
  similarityContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 4,
  },
  similarityDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: 6,
  },
  similarityText: {
    fontSize: 14,
    color: "#777777",
  },
  similarPatientList: {
    paddingBottom: 8,
  },

  // Form Styles
  formContainer: {
    width: "100%",
    maxWidth: 400,
    backgroundColor: "#F5F5F5",
    padding: 16,
    borderRadius: 8,
  },
  formLabel: {
    fontSize: 14,
    fontWeight: "500",
    color: "#555555",
    marginBottom: 8,
  },
  formInput: {
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#DDDDDD",
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    marginBottom: 16,
  },
  formButtons: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 16,
  },
  submitButton: {
    backgroundColor: "#0085FF",
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 6,
    flex: 1,
    marginRight: 8,
    alignItems: "center",
  },
  submitButtonText: {
    color: "#FFFFFF",
    fontSize: 14,
    fontWeight: "500",
  },
  cancelButton: {
    backgroundColor: "#FFFFFF",
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#DDDDDD",
    flex: 1,
    marginLeft: 8,
    alignItems: "center",
  },
  cancelButtonText: {
    color: "#555555",
    fontSize: 14,
    fontWeight: "500",
  },
});

export default PatientPlanningView;
